import { createSHA256 } from "hash-wasm";

// ~16MB slices: large enough to keep the WASM update loop efficient, small
// enough that we never hold more than one slice in memory — required for the
// 50GB-class files this app accepts.
const SLICE_BYTES = 16 * 1024 * 1024;

/**
 * Compute the lowercase-hex SHA-256 of a File without loading it all into
 * memory. Reads the file in fixed-size slices and feeds each to an incremental
 * hasher (Web Crypto can't stream, hence hash-wasm).
 *
 * onProgress reports the fraction of bytes hashed (0..1); always ends at 1.
 * If a signal is provided, hashing aborts between slices (throws AbortError)
 * so cancelling a large upload doesn't have to wait out the whole hash pass.
 */
export async function hashFileSha256(
  file: File,
  onProgress?: (frac: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");
  const hasher = await createSHA256();
  hasher.init();
  let offset = 0;
  // Zero-length files: report complete and return the empty-input digest.
  if (file.size === 0) {
    onProgress?.(1);
    return hasher.digest("hex");
  }
  while (offset < file.size) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    const end = Math.min(offset + SLICE_BYTES, file.size);
    const buf = new Uint8Array(await file.slice(offset, end).arrayBuffer());
    hasher.update(buf);
    offset = end;
    onProgress?.(offset / file.size);
  }
  return hasher.digest("hex");
}
